 
import { formatInTimeZone } from 'date-fns-tz';
import { TFunction } from 'i18next';
import { SuperbillSummary } from 'domains/superbillPDF/data/localStorage';
import { DEFAULT_TIMEZONE_OPTION, findTimezoneByResolvedName } from 'domains/timezone';
import { DAY_MONTH_YEAR, TIME } from 'utils/date';
import { Props } from '../ui';
import { calculatePayments } from './calculatePayments';
import { calculateInsuranceProps } from './insurance';
import { calculateSelfPayProps } from './self-pay';

export const calculateSuperbillPDFProps = (superbill: SuperbillSummary, t: TFunction): Props => {
  const {
    client,
    claim,
    clientDiagnoses: externalClientDiagnoses,
    medicalDiagnoses: externalMedicalDiagnoses,
    therapist: externalTherapist,
    contact,
    note,
    contractedBusiness: externalContractedBusiness,
    payment,
  } = superbill;
  const cptData = note.detail.cpt?.[0];

  const clientDiagnoses = note.clientDiagnoses ?? externalClientDiagnoses;
  const medicalDiagnoses = note.medicalDiagnoses ?? externalMedicalDiagnoses;
  const therapist = note.therapist ?? externalTherapist;
  const contractedBusiness = note.contractedBusiness ?? externalContractedBusiness;

  const contactTimezone =
    findTimezoneByResolvedName(note.contactTimeZone ?? contact.contactTimeZone) ?? DEFAULT_TIMEZONE_OPTION;

  const lockDate = new Date(note.lockedAt);
  const signatureDateWithoutTz = t('superbill.pdfPage.signature.date', {
    date: formatInTimeZone(lockDate, contactTimezone.value, DAY_MONTH_YEAR),
    time: formatInTimeZone(lockDate, contactTimezone.value, TIME),
  });

  const commonProps = {
    placeOfService: 'Telepractice-POS 10',
    modifier: 'GN, 95',
    billingFaxNumber: '(512) 546-6034',
    billingEmail: 'help@expressable.io',
    signatureDate: `${signatureDateWithoutTz} ${contactTimezone.shortName}`,
    clientDiagnosisDescription: clientDiagnoses[0].description,
    clientDiagnosisCode: clientDiagnoses[0].diagnosisCode,
    medicalDiagnoseDescription: medicalDiagnoses?.[0]?.description,
    medicalDiagnoseCode: medicalDiagnoses?.[0]?.code,
  };

  const { charge, paidByPatient } = calculatePayments({ claim, note, payment });

  if (claim) {
    const insuranceProps = calculateInsuranceProps({ claim, therapist, cptData, paidByPatient });

    return {
      ...commonProps,
      ...insuranceProps,
    };
  }

  const selfPayProps = calculateSelfPayProps({
    cptData,
    therapist,
    contractedBusiness,
    paidByPatient,
    client,
    contact,
    note,
    charge,
  });

  return {
    ...commonProps,
    ...selfPayProps,
  };
};
